"use client"

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

import "react-multi-carousel/lib/styles.css"
import "react-calendar/dist/Calendar.css"
import { Button, CircularProgress, Backdrop, Typography, Breadcrumbs, Link } from "@mui/material"
import { Dropdown } from "react-bootstrap"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"

import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import HighchartsExporting from "highcharts/modules/exporting"
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting"
import HighchartsFullscreen from "highcharts/modules/full-screen"
import highchartsMore from "highcharts/highcharts-more"
import SolidGauge from "highcharts/modules/solid-gauge"
import HighchartsMap from "highcharts/modules/map" // Map module
import mapDataWorld from "@highcharts/map-collection/custom/world.geo.json"
import proj4 from "proj4"

import { MultiSelect } from "primereact/multiselect"
import { ResponsiveContainer } from "recharts"

import LoginHeader from "./components/LoginHeader.js"
import SideNavigation from "./components/SideNavigation.js"
import AADHeader from "./components/AADHeader.js"

import { appInsights } from "./AppInsights"

import { MdRefresh } from "react-icons/md"

// Keep whole numbers for values >=1; preserve decimals for 0<|v|<1 so small emissions aren't charted as 0.
const toMetricNumber = (value) => {
  if (!Number.isFinite(value)) return 0
  if (Math.abs(value) >= 1) return Math.round(value)
  const twoDp = Number(value.toFixed(2))
  return twoDp !== 0 ? twoDp : Number(value.toPrecision(2))
}

// No Data Component
const NoDataComponent = ({ title, onConfigureClick }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "280px",
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px dashed #dee2e6",
      textAlign: "center",
      padding: "20px",
    }}
  >
    <div style={{ marginBottom: "20px" }}>
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M9 17H7V10H9M13 17H11V7H13M17 17H15V13H17M19.5 19.1H4.5V5H19.5M19.5 3H4.5C3.4 3 2.5 3.9 2.5 5V19.1C2.5 20.2 3.4 21.1 4.5 21.1H19.5C20.6 21.1 21.5 20.2 21.5 19.1V5C21.5 3.9 20.6 3 19.5 3Z"
          fill="#6c757d"
        />
      </svg>
    </div>
    <h6 style={{ color: "#6c757d", marginBottom: "10px", fontFamily: "Poppins" }}>No Data Available</h6>
    <p style={{ color: "#6c757d", marginBottom: "20px", fontSize: "14px" }}>Please configure the data for visual</p>
    <Button
      variant="contained"
      onClick={onConfigureClick}
      style={{
        backgroundColor: "#407775",
        color: "white",
        textTransform: "none",
        fontFamily: "Poppins",
        borderRadius: "6px",
        padding: "8px 20px",
      }}
    >
      Configure Data
    </Button>
  </div>
)

// Initialize the exporting module
HighchartsExporting(Highcharts)
HighchartsOfflineExporting(Highcharts)
HighchartsFullscreen(Highcharts)
HighchartsMap(Highcharts)
highchartsMore(Highcharts)
SolidGauge(Highcharts)
// Global Highcharts options for consistent styling
Highcharts.setOptions({
  colors: [
    "#407775",
    "#fab430",
    "#c8e1e0",
    "#b8d9d8",
    "#b8d9d8",
    "#b8d9d8",
    "#b8d9d8",
    "#fcd283",
    "#fde4b4",
    "#fbc051",
    "#e09405",
    "#fef6e6",
  ],
  chart: {
    style: {
      fontFamily: "Poppins, sans-serif",
    },
    backgroundColor: "#ffffff",
    height: 300, // Set a compact height for all charts
  },
  title: {
    style: {
      fontSize: "16px",
      fontWeight: "400",
      color: "#494949",
    },
    align: "left",
  },
  legend: {
    enabled: true,
    itemStyle: {
      color: "#494949",
      fontWeight: "400",
      fontSize: "12px",
    },
    itemMarginTop: 6,
    itemMarginBottom: 6,
    symbolRadius: 6,
  },
  tooltip: {
    style: {
      fontSize: "12px",
    },
  },
  xAxis: {
    labels: {
      style: {
        fontSize: "12px",
        color: "#707070",
      },
    },
  },
  yAxis: {
    labels: {
      style: {
        fontSize: "12px",
        color: "#707070",
      },
    },
    title: {
      style: {
        fontSize: "14px",
        color: "#C2BEBE",
      },
    },
  },
  credits: {
    enabled: false, // Disable Highcharts credit label
  },
  exporting: {
    enabled: false, // Hide export/download button
  },
})

// CSS for compact chart containers
const chartContainerStyle = `
  .charts {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 15px 15px 28px 15px; /* extra bottom padding to avoid touching legend */
    margin-bottom: 20px;
    height: 350px; /* Fixed height for compact charts */
    overflow: hidden;
  }
  @media (max-width: 768px) {
    .charts {
      height: 350px; /* Smaller height for mobile */
    }
  }
`

// Inject CSS into the document
const styleSheet = document.createElement("style")
styleSheet.type = "text/css"
styleSheet.innerText = chartContainerStyle
document.head.appendChild(styleSheet)

const countryNameToCode = {
  Afghanistan: "AF",
  Albania: "AL",
  Algeria: "DZ",
  Andorra: "AD",
  Angola: "AO",
  "Antigua and Barbuda": "AG",
  Argentina: "AR",
  Armenia: "AM",
  Australia: "AU",
  Austria: "AT",
  Azerbaijan: "AZ",
  Bahamas: "BS",
  Bahrain: "BH",
  Bangladesh: "BD",
  Barbados: "BB",
  Belarus: "BY",
  Belgium: "BE",
  Belize: "BZ",
  Benin: "BJ",
  Bhutan: "BT",
  Bolivia: "BO",
  "Bosnia and Herzegovina": "BA",
  Botswana: "BW",
  Brazil: "BR",
  Brunei: "BN",
  Bulgaria: "BG",
  "Burkina Faso": "BF",
  Burundi: "BI",
  "Cabo Verde": "CV",
  Cambodia: "KH",
  Cameroon: "CM",
  Canada: "CA",
  "Central African Republic": "CF",
  Chad: "TD",
  Chile: "CL",
  China: "CN",
  Colombia: "CO",
  Comoros: "KM",
  "Congo (Congo-Brazzaville)": "CG",
  "Costa Rica": "CR",
  Croatia: "HR",
  Cuba: "CU",
  Cyprus: "CY",
  Czechia: "CZ",
  "Democratic Republic of the Congo": "CD",
  Denmark: "DK",
  Djibouti: "DJ",
  Dominica: "DM",
  "Dominican Republic": "DO",
  Ecuador: "EC",
  Egypt: "EG",
  "El Salvador": "SV",
  "Equatorial Guinea": "GQ",
  Eritrea: "ER",
  Estonia: "EE",
  Eswatini: "SZ",
  Ethiopia: "ET",
  Fiji: "FJ",
  Finland: "FI",
  France: "FR",
  Gabon: "GA",
  Gambia: "GM",
  Georgia: "GE",
  Germany: "DE",
  Ghana: "GH",
  Greece: "GR",
  Grenada: "GD",
  Guatemala: "GT",
  Guinea: "GN",
  "Guinea-Bissau": "GW",
  Guyana: "GY",
  Haiti: "HT",
  Honduras: "HN",
  Hungary: "HU",
  Iceland: "IS",
  India: "IN",
  Indonesia: "ID",
  Iran: "IR",
  Iraq: "IQ",
  Ireland: "IE",
  Israel: "IL",
  Italy: "IT",
  Jamaica: "JM",
  Japan: "JP",
  Jordan: "JO",
  Kazakhstan: "KZ",
  Kenya: "KE",
  Kiribati: "KI",
  Kuwait: "KW",
  Kyrgyzstan: "KG",
  Laos: "LA",
  Latvia: "LV",
  Lebanon: "LB",
  Lesotho: "LS",
  Liberia: "LR",
  Libya: "LY",
  Liechtenstein: "LI",
  Lithuania: "LT",
  Luxembourg: "LU",
  Madagascar: "MG",
  Malawi: "MW",
  Malaysia: "MY",
  Maldives: "MV",
  Mali: "ML",
  Malta: "MT",
  "Marshall Islands": "MH",
  Mauritania: "MR",
  Mauritius: "MU",
  Mexico: "MX",
  Micronesia: "FM",
  Moldova: "MD",
  Monaco: "MC",
  Mongolia: "MN",
  Montenegro: "ME",
  Morocco: "MA",
  Mozambique: "MZ",
  "Myanmar (Burma)": "MM",
  Namibia: "NA",
  Nauru: "NR",
  Nepal: "NP",
  Netherlands: "NL",
  "New Zealand": "NZ",
  Nicaragua: "NI",
  Niger: "NE",
  Nigeria: "NG",
  "North Korea": "KP",
  "North Macedonia": "MK",
  Norway: "NO",
  Oman: "OM",
  Pakistan: "PK",
  Palau: "PW",
  "Palestine State": "PS",
  Panama: "PA",
  "Papua New Guinea": "PG",
  Paraguay: "PY",
  Peru: "PE",
  Philippines: "PH",
  Poland: "PL",
  Portugal: "PT",
  Qatar: "QA",
  Romania: "RO",
  Russia: "RU",
  Rwanda: "RW",
  "Saint Kitts and Nevis": "KN",
  "Saint Lucia": "LC",
  "Saint Vincent and the Grenadines": "VC",
  Samoa: "WS",
  "San Marino": "SM",
  "Sao Tome and Principe": "ST",
  "Saudi Arabia": "SA",
  Senegal: "SN",
  Serbia: "RS",
  Seychelles: "SC",
  "Sierra Leone": "SL",
  Singapore: "SG",
  Slovakia: "SK",
  Slovenia: "SI",
  "Solomon Islands": "SB",
  Somalia: "SO",
  "South Africa": "ZA",
  "South Korea": "KR",
  "South Sudan": "SS",
  Spain: "ES",
  "Sri Lanka": "LK",
  Sudan: "SD",
  Suriname: "SR",
  Sweden: "SE",
  Switzerland: "CH",
  Syria: "SY",
  Taiwan: "TW",
  Tajikistan: "TJ",
  Tanzania: "TZ",
  Thailand: "TH",
  "Timor-Leste": "TL",
  Togo: "TG",
  Tonga: "TO",
  "Trinidad and Tobago": "TT",
  Tunisia: "TN",
  Turkey: "TR",
  Turkmenistan: "TM",
  Tuvalu: "TV",
  Uganda: "UG",
  Ukraine: "UA",
  "United Arab Emirates": "AE",
  "United Kingdom": "GB",
  "United States": "US",
  Uruguay: "UY",
  Uzbekistan: "UZ",
  Vanuatu: "VU",
  "Vatican City": "VA",
  Venezuela: "VE",
  Vietnam: "VN",
  Yemen: "YE",
  Zambia: "ZM",
  Zimbabwe: "ZW",
  // Add more or adjust as needed
}

export default function Home() {
  const username = localStorage.getItem("userName")
  const companyid = localStorage.getItem("companyid")
  const navigate = useNavigate()
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768)
  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = React.useState(false)
  const [emissionbased, setEmissionBased] = React.useState(false)
  const [news, setNews] = useState(false)
  const [responseConfig, setResponseConfig] = useState({})
  const [legal, setLegal] = useState([])
  const [business, setBusiness] = useState([])
  const [site, setSite] = useState([])
  const [year, setYear] = useState([])
  const [country, setCountry] = useState([])
  const [employee, setEmployee] = useState("")
  const [transport, setTransport] = useState("")
  const [yearr, setYearR] = useState("")
  const [countries, setCountries] = useState()
  const [businessunit, setBusinessUnit] = useState()
  const [activesite, setActiveSite] = useState()
  const [activeetities, setActiveEntities] = useState()
  const [activeyears, setActiveYears] = useState()
  const [checked, setChecked] = useState(true)
  const [viewMode, setViewMode] = useState("locationBased")

  // Add state for last refresh time
  const [lastRefreshTime, setLastRefreshTime] = useState(null)

  if (typeof window !== "undefined") {
    window.proj4 = window.proj4 || proj4
  }

  // Function to check if data exists and has values
  const hasValidData = (data) => {
    if (!data || typeof data !== "object") return false
    if (Array.isArray(data)) return data.length > 0
    return Object.keys(data).length > 0 && Object.values(data).some((value) => value > 0)
  }

  // Function to handle configuration redirect
  const handleConfigureClick = () => {
    navigate("/configuration") // Update this path to your actual configuration route
  }

  // Function to format date and time
  const formatDateTime = (date) => {
    if (!date) return ""
    const options = {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }
    return date.toLocaleString("en-US", options)
  }

  // Function to handle toggle change
  const handleToggleChange = (value) => {
    console.log(value)
    if (value === true) {
      setViewMode("locationBased")
      setChecked(value)
    } else {
      setViewMode("marketBased")
      setChecked(value)
    }
  }

  function truncateText(text, maxLength) {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "..."
    }
    return text
  }

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev)
  }

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false) // Close sidebar on mobile
      } else {
        setIsSidebarOpen(true) // Open sidebar on larger screens
      }
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  var myHeaders = new Headers()
  myHeaders.append("username", username)
  var requestOptions = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  }

  const emissionsData = [
    {
      title: "Upstream transportation and distribution emissions (tCO2e)",
      data2023: transport?.totalUpstreamEmission || 0,
      data2022: transport?.totalUpstreamEmissionForPreviousYear || 0,
      change: transport?.upstreamChange || 0,
      isIncrease: transport?.upstreamChange < 0,
    },
    {
      title: "Downstream transportation and distribution emissions (tCO2e)",
      data2023: transport?.totalDownstreamEmission || 0,
      data2022: transport?.totalDownstreamEmissionForPreviousYear || 0,
      change: transport?.downstreamChange || 0,
      isIncrease: transport?.downstreamChange < 0,
    },
  ]

  async function fetchFilters() {
    try {
      const country = JSON.parse(localStorage.getItem("transportcountry")) || []
      const year = JSON.parse(localStorage.getItem("transportyear")) || []
      const legal = JSON.parse(localStorage.getItem("transportlegal")) || []
      const business = JSON.parse(localStorage.getItem("transportbusiness")) || []
      const site = JSON.parse(localStorage.getItem("transportsite")) || []

      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}transportation-dashboard-filters`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          companyId: companyid,
          year: year,
          legalEntity: legal,
          businessUnit: business,
          country: country,
          siteLocation: site,
        }),
      })

      if (response.status === 200) {
        const responseJson = await response.json()

        if (responseJson.success === false || responseJson.status === 400) {
          setActiveEntities("")
          setActiveYears("")
          setBusinessUnit("")
          setCountries("")
          setActiveSite("")
        } else {
          let legalOptions
          let businessOptions
          let countryOptions
          let siteOptions
          let yearOptions
          // Check if any filters are selected
          const isAnyFilterSelected =
            year.length > 0 || legal.length > 0 || business.length > 0 || country.length > 0 || site.length > 0

          // Generate dropdown options based on whether any filters are selected
          if (
            year.length > 0 &&
            legal.length === 0 &&
            business.length === 0 &&
            country.length === 0 &&
            site.length === 0
          ) {
            yearOptions = Object.entries(responseJson.year).map(([key]) => ({
              label: key,
              value: key,
            }))
          } else {
            yearOptions = Object.entries(responseJson.year)
              .map(([key]) => ({
                label: key,
                value: key,
              }))
          }

          if (
            year.length === 0 &&
            legal.length > 0 &&
            business.length === 0 &&
            country.length === 0 &&
            site.length === 0
          ) {
            legalOptions = Object.entries(responseJson.legalEntity).map(([key]) => ({
              label: key,
              value: key,
            }))
          } else {
            legalOptions = Object.entries(responseJson.legalEntity)
              .map(([key]) => ({
                label: key,
                value: key,
              }))
          }

          if (
            year.length === 0 &&
            legal.length === 0 &&
            business.length > 0 &&
            country.length === 0 &&
            site.length === 0
          ) {
            businessOptions = Object.entries(responseJson.businessUnit).map(([key]) => ({
              label: key,
              value: key,
            }))
          } else {
            businessOptions = Object.entries(responseJson.businessUnit)
              .map(([key]) => ({
                label: key,
                value: key,
              }))
          }

          if (
            year.length === 0 &&
            legal.length === 0 &&
            business.length === 0 &&
            country.length > 0 &&
            site.length === 0
          ) {
            countryOptions = Object.entries(responseJson.country).map(([key]) => ({
              label: key,
              value: key,
            }))
          } else {
            countryOptions = Object.entries(responseJson.country)
              .map(([key]) => ({
                label: key,
                value: key,
              }))
          }

          if (
            year.length === 0 &&
            legal.length === 0 &&
            business.length === 0 &&
            country.length === 0 &&
            site.length > 0
          ) {
            siteOptions = Object.entries(responseJson.siteLocation).map(([key]) => ({
              label: key,
              value: key,
            }))
          } else {
            siteOptions = Object.entries(responseJson.siteLocation)
              .map(([key]) => ({
                label: key,
                value: key,
              }))
          }
          // Set the dropdown options
          setActiveYears(yearOptions)
          setActiveEntities(legalOptions)
          setBusinessUnit(businessOptions)
          setCountries(countryOptions)
          setActiveSite(siteOptions)

          // Update local storage with selected values
          localStorage.setItem("transportlegal", JSON.stringify(legal))
          setLegal(legal)

          localStorage.setItem("transportbusiness", JSON.stringify(business))
          setBusiness(business)

          localStorage.setItem("transportcountry", JSON.stringify(country))
          setCountry(country)

          localStorage.setItem("transportsite", JSON.stringify(site))
          setSite(site)

          localStorage.setItem("transportyear", JSON.stringify(year))
          setYear(year)
        }
      } else {
        setActiveEntities("")
        setActiveYears("")
        setBusinessUnit("")
        setCountries("")
        setActiveSite("")
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function fetchDashboard() {
    try {
      setLoading(true)
      const companyid = localStorage.getItem("companyid")
      const country = JSON.parse(localStorage.getItem("transportcountry")) || []
      const year = JSON.parse(localStorage.getItem("transportyear")) || []
      const legal = JSON.parse(localStorage.getItem("transportlegal")) || []
      const business = JSON.parse(localStorage.getItem("transportbusiness")) || []
      const site = JSON.parse(localStorage.getItem("transportsite")) || []
      var myHeaders = new Headers()
      myHeaders.append("username", username)
      var requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      }
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}transportation-dashboard`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          companyId: companyid,
          year: year,
          legalEntity: legal,
          businessUnit: business,
          country: country,
          siteLocation: site,
        }),
      })
      if (response.status === 200) {
        const responseJson = await response.json()
        if (responseJson.success === false || responseJson.status === 400) {
          setLoading(false)
        } else {
          setTransport(responseJson)
          // Update last refresh time when data is successfully fetched
          setLastRefreshTime(new Date())
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }

  useEffect(() => {
    appInsights.trackPageView()
    fetchFilters()
    fetchDashboard()
  }, [])

  const handleLegalChange = (selectedLegal) => {
    setLegal(selectedLegal)
    localStorage.setItem("transportlegal", JSON.stringify(selectedLegal))
    fetchDashboard()
    fetchFilters()
  }

  const handleBusinessChange = (selectedBusiness) => {
    setBusiness(selectedBusiness)
    localStorage.setItem("transportbusiness", JSON.stringify(selectedBusiness))
    fetchDashboard()
    fetchFilters()
  }

  const handleCountryChange = (selectedCountry) => {
    setCountry(selectedCountry)
    localStorage.setItem("transportcountry", JSON.stringify(selectedCountry))
    fetchDashboard()
    fetchFilters()
  }

  const handleSiteChange = (selectedSite) => {
    setSite(selectedSite)
    localStorage.setItem("transportsite", JSON.stringify(selectedSite))
    fetchDashboard()
    fetchFilters()
  }

  const handleYearChange = (selectedYear) => {
    setYear(selectedYear)
    localStorage.setItem("transportyear", JSON.stringify(selectedYear))
    fetchDashboard()
    fetchFilters()
  }

  const activeyear = activeyears && activeyears.map((status) => status)
  console.log(activeyear)
  const activelegal = activeetities && activeetities.map((status) => status)
  const activebusiness = businessunit && businessunit.map((status) => status)
  const activesites = activesite && activesite.map((status) => status)
  const activecountry = countries && countries.map((status) => status)
  console.log(activecountry)
  //const activeyear = yearss.map((status) => status.name);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType")
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
  }

  const handleResetFilters = async () => {
    try {
      setLoading(true)
      // Clear all selected filters in state
      setLegal([])
      setBusiness([])
      setCountry([])
      setSite([])
      setYear([])

      // Clear all selected filters in local storage
      localStorage.removeItem("transportlegal")
      localStorage.removeItem("transportbusiness")
      localStorage.removeItem("transportcountry")
      localStorage.removeItem("transportsite")
      localStorage.removeItem("transportyear")

      // Fetch filters again to reset dropdowns to show all values
      await fetchFilters()

      // Fetch transport 2 data again with no filters applied
      await fetchDashboard()
    } catch (error) {
      console.error("Error resetting filters:", error)
    }
  }

  // Handle manual refresh
  const handleRefresh = () => {
    fetchDashboard()
    fetchFilters()
  }

  const [selectedOption, setSelectedOption] = useState("Upstream")
  const [selectedOption2, setSelectedOption2] = useState("Upstream")
  const [selectedOption3, setSelectedOption3] = useState("Upstream")
  const [selectedOption4, setSelectedOption4] = useState("Upstream")
  const [selectedOption5, setSelectedOption5] = useState("Upstream")
  const handleSelect = (option) => {
    setSelectedOption(option)
  }

  const handleSelect2 = (option) => {
    setSelectedOption2(option)
  }

  const handleSelect3 = (option) => {
    setSelectedOption3(option)
  }

  const handleSelect4 = (option) => {
    setSelectedOption4(option)
  }

  const handleSelect5 = (option) => {
    setSelectedOption5(option)
  }

  // Function to assign colors based on transport mode
  const getColorForMode = (mode) => {
    const colorMap = {
      "Air Transport": "#407775",
      "Marine Transport": "#fab430",
      "Rail Transport": "#c8e1e0",
      "Road Transport": "#b8d9d8",
      Storage: "#fcd283",
      Fugitive: "#fde4b4",
    }
    return colorMap[mode] || "#fef6e6" // Default color if mode not found
  }

  const dataUpstream = Object.entries(transport.upstreamEmissionsShareByMode || {}).map(([name, y]) => ({
    name,
    y,
    color: getColorForMode(name), // You can define a function to assign colors based on mode
  }))

  const dataDownstream = Object.entries(transport.downstreamEmissionsShareByMode || {}).map(([name, y]) => ({
    name,
    y,
    color: getColorForMode(name),
  }))

  const selectedData = selectedOption === "Upstream" ? dataUpstream : dataDownstream

  // Check if chart 1 has valid data
  const hasChart1Data = hasValidData(selectedData) && selectedData.some((item) => item.y > 0)

  const chartConfig = {
    chart: {
      type: "pie",
      spacingBottom: 24,
    },
    title: {
      text: null,
    },
    tooltip: {
      pointFormat: "<b>{point.y}%</b>",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "fixedOffset",
        dataLabels: {
          enabled: true,
          format: "{point.y}%",
          connectorColor: "#707070",
          distance: 20,
          style: {
            color: "#707070",
            fontSize: "12px",
            fontWeight: "400",
          },
        },
        showInLegend: true,
      },
    },
    legend: {
      enabled: document.documentElement.clientWidth >= 768,
      layout: "vertical",
      align: "left",
      verticalAlign: "middle",
      borderWidth: 0,
      itemStyle: {
        color: "#494949",
        fontWeight: "400",
        fontSize: "12px",
      },
      itemMarginTop: 6,
      itemMarginBottom: 6,
      x: -10,
      y: -100,
      symbolRadius: 10,
    },
    series: [
      {
        name: "Share",
        colorByPoint: true,
        data: selectedData,
      },
    ],
  }
  //Chart2
  const dataUpstream2 = {
    categories: Object.keys(transport.upstreamEmissionsIntensityByTransportMode || {}),
    emissionsIntensity: Object.values(transport.upstreamEmissionsIntensityByTransportMode || {}).map((value) =>
      toMetricNumber(value),
    ),
    shipments: Object.values(transport.upstreamNoOfShipmentsByTransportMode || {}).map((value) => Math.round(value)),
  }

  const dataDownstream2 = {
    categories: Object.keys(transport.downstreamEmissionsIntensityByTransportMode || {}),
    emissionsIntensity: Object.values(transport.downstreamEmissionsIntensityByTransportMode || {}).map((value) =>
      toMetricNumber(value),
    ),
    shipments: Object.values(transport.downstreamNoOfShipmentsByTransportMode || {}).map((value) => Math.round(value)),
  }

  const selectedData2 = selectedOption2 === "Upstream" ? dataUpstream2 : dataDownstream2

  // Check if chart 2 has valid data
  const hasChart2Data =
    hasValidData(selectedData2.categories) &&
    (selectedData2.emissionsIntensity.some((val) => val > 0) || selectedData2.shipments.some((val) => val > 0))

  const chartConfig2 = {
    chart: {
      zoomType: "xy",
      spacingBottom: 28,
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: selectedData2.categories,
      crosshair: true,
      labels: {
        rotation: document.documentElement.clientWidth <= 885 ? 270 : undefined,
        style: {
          color: "#707070",
          fontSize: "12px",
        },
      },
    },
    yAxis: [
      {
        title: {
          text: "Emissions Intensity (gCO2e/t-km)",
          style: {
            color: "#C2BEBE",
            fontSize: "14px",
          },
          x: -10,
        },
        labels: {
          format: "{value}",
          style: {
            color: "#707070",
            fontSize: "12px",
          },
        },
        opposite: false,
        tickInterval: Math.ceil(Math.max(...selectedData2.emissionsIntensity) / 5),
      },
      {
        title: {
          text: "No. of shipments",
          style: {
            color: "#C2BEBE",
            fontSize: "14px",
          },
          rotation: -90,
          x: 10,
        },
        labels: {
          format: "{value}",
          style: {
            color: "#707070",
            fontSize: "12px",
          },
        },
        opposite: true,
        tickInterval: Math.ceil(Math.max(...selectedData2.shipments) / 5),
      },
    ],
    tooltip: {
      shared: true,
    },
    legend: {
      enabled: document.documentElement.clientWidth >= 768,
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    series: [
      {
        name: "Emissions Intensity (gCO2e/t-km)",
        type: "column",
        yAxis: 0,
        data: selectedData2.emissionsIntensity,
        tooltip: {
          valueSuffix: " gCO2e/t-km",
        },
        color: "#F5A623",
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "12px",
            color: "#707070",
            fontWeight: "normal",
          },
          formatter: function () {
            return this.y
          },
        },
      },
      {
        name: "No. of shipments",
        type: "spline",
        yAxis: 1,
        data: selectedData2.shipments,
        tooltip: {
          valueSuffix: "",
        },
        color: "#2C6B6F",
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "12px",
            color: "#707070",
            fontWeight: "normal",
          },
          formatter: function () {
            return this.y
          },
        },
      },
    ],
  }
  //chart3
  const dataUpstream3 = Object.entries(transport.upstreamTotalEmissionsByCountry || {}).map(([country, value]) => ({
    code: countryNameToCode[country] || country,
    value: toMetricNumber(value),
    name: country,
  }))

  const dataDownstream3 = Object.entries(transport.downstreamTotalEmissionsByCountry || {}).map(([country, value]) => ({
    code: countryNameToCode[country] || country,
    value: toMetricNumber(value),
    name: country,
  }))

  const selectedData3 = selectedOption3 === "Upstream" ? dataUpstream3 : dataDownstream3

  // Check if chart 3 has valid data
  const hasChart3Data = hasValidData(selectedData3) && selectedData3.some((item) => item.value > 0)

  const chartConfig3 = {
    chart: {
      map: mapDataWorld,
      spacingBottom: 24,
    },
    title: {
      text: null,
      align: "left",
    },
    colorAxis: {
      width: "20%",
      height: 25,
      min: Math.min(...selectedData3.map((item) => item.value)),
      max: Math.max(...selectedData3.map((item) => item.value)),
      stops: [
        [0, "#FFEDC0"],
        [1, "#CE992A"],
      ],
      margin: 10,
      labels: {
        enabled: true,
        align: "right",
        formatter: function () {
          // Only show labels for min and max values
          if (this.value === this.axis.min || this.value === this.axis.max) {
            return this.value
          }
          return ""
        },
      },
      tickPositions: [
        Math.min(...selectedData3.map((item) => item.value)),
        Math.max(...selectedData3.map((item) => item.value)),
      ],
    },
    tooltip: {
      headerFormat: "",
      pointFormat: "<b>{point.name}</b>: {point.value} tCO2e",
    },
    legend: {
      layout: "horizontal",
      align: "right",
      verticalAlign: "bottom",
    },
    series: [
      {
        data: selectedData3.map((item) => ({
          "hc-key": item.code.toLowerCase(),
          value: item.value,
          name: item.name,
        })),
        mapData: mapDataWorld,
        joinBy: "hc-key",
        name: "Emissions (tCO2e)",
        states: {
          hover: {
            color: "#E63946",
          },
        },
        dataLabels: {
          enabled: false,
        },
      },
    ],
  }
  //chart4
  const dataUpstream4 = {
    categories: Object.keys(transport.upstreamTotalEmissionsByLegalEntity || {}),
    emissions: Object.values(transport.upstreamTotalEmissionsByLegalEntity || {}).map((value) => toMetricNumber(value)),
  }

  const dataDownstream4 = {
    categories: Object.keys(transport.downstreamTotalEmissionsByLegalEntity || {}),
    emissions: Object.values(transport.downstreamTotalEmissionsByLegalEntity || {}).map((value) => toMetricNumber(value)),
  }

  const selectedData4 = selectedOption4 === "Upstream" ? dataUpstream4 : dataDownstream4

  // Check if chart 4 has valid data
  const hasChart4Data = hasValidData(selectedData4.categories) && selectedData4.emissions.some((val) => val > 0)

  const chartConfig4 = {
    chart: {
      type: "bar",
      spacingBottom: 24,
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: selectedData4.categories,
      title: {
        text: null,
      },
      labels: {
        style: {
          color: "#707070",
          fontSize: "12px",
        },
        rotation: -45,
        align: "right",
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: "Emissions (tCO2e)",
        align: "middle",
        style: {
          color: "#C2BEBE",
          fontSize: "16px",
        },
      },
      labels: {
        overflow: "justify",
        style: {
          color: "#707070",
          fontSize: "12px",
        },
      },
      tickInterval: Math.ceil(Math.max(...selectedData4.emissions) / 5),
    },
    tooltip: {
      valueSuffix: " tCO2e",
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          style: {
            color: "#707070",
            fontWeight: "normal",
            fontSize: "12px",
          },
        },
        borderRadius: 3,
      },
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: "Emissions",
        data: selectedData4.emissions,
        color: "#B4A34A",
      },
    ],
  }
  //chart5
  const dataUpstream5 = {
    categories: Object.keys(transport.upstreamTotalEmissionsByBusinessUnit || {}),
    emissions: Object.values(transport.upstreamTotalEmissionsByBusinessUnit || {}).map((value) => toMetricNumber(value)),
  }

  const dataDownstream5 = {
    categories: Object.keys(transport.downstreamTotalEmissionsByBusinessUnit || {}),
    emissions: Object.values(transport.downstreamTotalEmissionsByBusinessUnit || {}).map((value) => toMetricNumber(value)),
  }

  const selectedData5 = selectedOption5 === "Upstream" ? dataUpstream5 : dataDownstream5

  // Check if chart 5 has valid data
  const hasChart5Data = hasValidData(selectedData5.categories) && selectedData5.emissions.some((val) => val > 0)

  const chartConfig5 = {
    chart: {
      type: "column",
      spacingBottom: 24,
    },
    title: {
      text: null,
    },
    xAxis: {
      labels: {
        rotation: document.documentElement.clientWidth <= 885 ? 270 : undefined,
        style: {
          color: "#707070",
          fontSize: "12px",
        },
      },
      categories: selectedData5.categories,
      title: {
        text: null,
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: "Emissions (tCO2e)",
        style: {
          color: "#C2BEBE",
          fontSize: "14px",
        },
      },
      labels: {
        overflow: "justify",
        style: {
          color: "#707070",
          fontSize: "12px",
        },
      },
      tickInterval: Math.ceil(Math.max(...selectedData5.emissions) / 5),
    },
    tooltip: {
      valueSuffix: " tCO2e",
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          style: {
            color: "#707070",
            fontWeight: "normal",
            fontSize: "14px",
          },
        },
        borderRadius: 3,
      },
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: "Emissions",
        data: selectedData5.emissions,
        color: "#A4B6AC",
      },
    ],
  }

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        {/* Rest of your existing JSX content */}
        <div
          class="container"
          style={{ marginLeft: "auto", marginRight: "auto", paddingLeft: "30px", paddingRight: "20px" }}
        >
          <div className="row">
            <div className="col-12 col-md-8">
              <div Class="steps__containt-hader">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <h3 class="title mb-0">Transportation and Distribution</h3>
                  {/* Add Last Refresh Time Display inline with heading */}
                  {lastRefreshTime && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#707070",
                        fontWeight: "400",
                      }}
                    >
                      Last Refresh: {formatDateTime(lastRefreshTime)}
                    </div>
                  )}
                </div>
                <div role="presentation" className="mt-2">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/esg-dashboard" className="link">
                      Dashboard
                    </Link>
                    <Typography color="text.primary">Transportation and Distribution</Typography>
                  </Breadcrumbs>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4 d-flex align-items-center justify-content-md-end flex-wrap gap-2">
              <div className="row justify-content-md-end my-2">
                <div className="col-auto">
                  <Button className="refresh-cta" variant="text" startIcon={<MdRefresh />} onClick={handleRefresh}>
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            {[
              {
                label: "Legal Entity",
                value: legal,
                setValue: setLegal,
                handleChange: handleLegalChange,
                options: activelegal,
              },
              {
                label: "Business Unit",
                value: business,
                setValue: setBusiness,
                handleChange: handleBusinessChange,
                options: activebusiness,
              },
              { label: "Site", value: site, setValue: setSite, handleChange: handleSiteChange, options: activesites },
              {
                label: "Country",
                value: country,
                setValue: setCountry,
                handleChange: handleCountryChange,
                options: activecountry,
              },
              { label: "Year", value: year, setValue: setYear, handleChange: handleYearChange, options: activeyear },
            ].map(({ label, value, setValue, handleChange, options }, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-2">
                <label className="form-label">{label}</label>
                <MultiSelect
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value)
                    handleChange(e.target.value)
                  }}
                  options={options}
                  placeholder="All"
                  maxSelectedLabels={1}
                  className="w-100"
                  showClear
                  showSelectAll
                  selectAllLabel="Select All"
                />
              </div>
            ))}

            <div className="col-12 col-sm-6 col-md-4 col-lg-2 d-flex align-items-end">
              <Button
                onClick={handleResetFilters}
                variant="outlined"
                startIcon={<MdRefresh />}
                sx={{
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "400",
                  textTransform: "none",
                  minHeight: "40px",
                  width: "100%",
                  "&:hover": {
                    backgroundColor: "#e8e8e8",
                    border: "1px solid #ccc",
                  },
                  "&:focus": {
                    outline: "none",
                    boxShadow: "0 0 0 2px rgba(0,123,255,.25)",
                  },
                }}
              >
                Reset
              </Button>
            </div>
          </div>
          {transport && (
            <div>
              <div className="row mt-4">
                {emissionsData.map((data, index) => (
                  <div key={index} className="col-12 col-md-6 mb-4">
                    <div
                      className="card border-0 shadow  emissions-card"
                      style={{ borderRadius: "0px", padding: "30px" }}
                    >
                      {/* Image and Title*/}
                      <div className="row align-items-center mb-3">
                        <div className="col-auto">
                          <img
                            src="../assets/img/icons 1/Transportation_Distribution_truck_icon.svg"
                            alt=""
                            className="emissions-icon"
                          />
                        </div>
                        <div className="col">
                          <h6
                            className=" mb-0 emissions-title  text-start"
                            style={{ color: "#707070", fontWeight: 600, fontSize: "16px", width: "90%" }}
                          >
                            {data.title}
                          </h6>
                        </div>
                      </div>

                      {/* Other details */}
                      <div className="d-flex justify-content-between align-items-end">
                        <div className="text-center">
                          <h4 className="fw-bold mb-0 data" style={{ fontSize: "18px", color: "#494949" }}>
                            {data.data2023.toLocaleString()}
                          </h4>
                          <small className="text-muted data-heading" style={{ color: "#707070", fontSize: "12px" }}>
                            {new Date().getFullYear()}
                          </small>
                        </div>
                        <div className="text-center">
                          <h4 className="fw-bold mb-0 data" style={{ fontSize: "18px", color: "#494949" }}>
                            {data.data2022.toLocaleString()}
                          </h4>
                          <small className="text-muted data-heading" style={{ color: "#707070", fontSize: "12px" }}>
                            {new Date().getFullYear() - 1}
                          </small>
                        </div>
                        <div className="text-center" style={{ marginTop: "0px" }}>
                          <h4
                            className={` mb-0 ${data.change > 0 ? "up-change" : "down-change"} `}
                            style={{ fontSize: "18px" }}
                          >
                            <img
                              src={
                                data.change > 0
                                  ? "../assets/img/icons 1/GreenDown.svg"
                                  : "../assets/img/icons 1/RedUp.svg"
                              }
                              alt=""
                              style={{ height: "15px" }}
                            />{" "}
                            {Math.abs(data.change)}%
                          </h4>
                          <small
                            className="text-muted mt-1 data-heading"
                            style={{ color: "#707070", fontSize: "12px" }}
                          >
                            Change
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts Section */}
              <div className="row">
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ width: "100%" }}>
                      <span
                        className="transportation-distribution-chart-title"
                        style={{
                          fontSize: "16px",
                          color: "#494949",
                          fontWeight: "400",
                          textAlign: "start",
                        }}
                      >
                        Emissions share by mode
                      </span>

                      <Dropdown onSelect={handleSelect}>
                        <Dropdown.Toggle
                          as="div"
                          style={{
                            backgroundColor: "#F5F5F5",
                            border: "1px solid #E3EAE9",
                            borderRadius: "5px",
                            height: "38.33px",
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            fontSize: "14px",
                            justifyContent: "space-between",
                          }}
                        >
                          {selectedOption}
                          <span style={{ display: "inline-flex", alignItems: "center" }}></span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item eventKey="Upstream">Upstream</Dropdown.Item>
                          <Dropdown.Item eventKey="Downstream">Downstream</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {hasChart1Data ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} options={chartConfig} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Emissions Share by Mode" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>

                <div className="col-12 col-lg-6">
                  <div className="charts">
                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ width: "100%" }}>
                      <span
                        className="transportation-distribution-chart-title"
                        style={{
                          fontSize: "16px",
                          color: "#494949",
                          fontWeight: "400",
                          textAlign: "start",
                        }}
                      >
                        No. of shipments and emissions intensity by transport mode
                      </span>

                      <Dropdown onSelect={handleSelect2}>
                        <Dropdown.Toggle
                          as="div"
                          style={{
                            backgroundColor: "#F5F5F5",
                            border: "1px solid #E3EAE9",
                            borderRadius: "5px",
                            height: "38.33px",
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            fontSize: "14px",
                            justifyContent: "space-between",
                          }}
                        >
                          {selectedOption2}
                          <span style={{ display: "inline-flex", alignItems: "center" }}></span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item eventKey="Upstream">Upstream</Dropdown.Item>
                          <Dropdown.Item eventKey="Downstream">Downstream</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {hasChart2Data ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} options={chartConfig2} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent
                        title="Shipments and Emissions Intensity"
                        onConfigureClick={handleConfigureClick}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Energy Consumption & Emission */}
              <div className="row">
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ width: "100%" }}>
                      <span
                        className="transportation-distribution-chart-title"
                        style={{
                          fontSize: "16px",
                          color: "#494949",
                          fontWeight: "400",
                          textAlign: "start",
                        }}
                      >
                        Emissions (tCO2e) by source country
                      </span>

                      <Dropdown onSelect={handleSelect3}>
                        <Dropdown.Toggle
                          as="div"
                          style={{
                            backgroundColor: "#F5F5F5",
                            border: "1px solid #E3EAE9",
                            borderRadius: "5px",
                            height: "38.33px",
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            fontSize: "14px",
                            justifyContent: "space-between",
                          }}
                        >
                          {selectedOption3}
                          <span style={{ display: "inline-flex", alignItems: "center" }}></span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item eventKey="Upstream">Upstream</Dropdown.Item>
                          <Dropdown.Item eventKey="Downstream">Downstream</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {hasChart3Data ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} constructorType="mapChart" options={chartConfig3} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Emissions by Source Country" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>

                <div className="col-12 col-lg-6">
                  <div className="charts">
                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ width: "100%" }}>
                      <span
                        className="transportation-distribution-chart-title"
                        style={{
                          fontSize: "16px",
                          color: "#494949",
                          fontWeight: "400",
                          textAlign: "start",
                        }}
                      >
                        Emissions (tCO2e) by legal entity
                      </span>

                      <Dropdown onSelect={handleSelect4}>
                        <Dropdown.Toggle
                          as="div"
                          style={{
                            backgroundColor: "#F5F5F5",
                            border: "1px solid #E3EAE9",
                            borderRadius: "5px",
                            height: "38.33px",
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            fontSize: "14px",
                            justifyContent: "space-between",
                          }}
                        >
                          {selectedOption4}
                          <span style={{ display: "inline-flex", alignItems: "center" }}></span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item eventKey="Upstream">Upstream</Dropdown.Item>
                          <Dropdown.Item eventKey="Downstream">Downstream</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {hasChart4Data ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} options={chartConfig4} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Emissions by Legal Entity" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
              </div>

              {/* Final Emission Graphs */}
              <div className="row">
                <div className="col-12 col-lg-6">
                  <div className="charts">
                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ width: "100%" }}>
                      <span
                        className="transportation-distribution-chart-title"
                        style={{
                          fontSize: "16px",
                          color: "#494949",
                          fontWeight: "400",
                          textAlign: "start",
                        }}
                      >
                        Emissions (tCO2e) by business unit
                      </span>

                      <Dropdown onSelect={handleSelect5}>
                        <Dropdown.Toggle
                          as="div"
                          style={{
                            backgroundColor: "#F5F5F5",
                            border: "1px solid #E3EAE9",
                            borderRadius: "5px",
                            height: "38.33px",
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            fontSize: "14px",
                            justifyContent: "space-between",
                          }}
                        >
                          {selectedOption5}
                          <span style={{ display: "inline-flex", alignItems: "center" }}></span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item eventKey="Upstream">Upstream</Dropdown.Item>
                          <Dropdown.Item eventKey="Downstream">Downstream</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {hasChart5Data ? (
                      <ResponsiveContainer width="100%">
                        <HighchartsReact highcharts={Highcharts} options={chartConfig5} />
                      </ResponsiveContainer>
                    ) : (
                      <NoDataComponent title="Emissions by Business Unit" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
                <div className="col-12 col-lg-6"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
